/**
 * SpeedDatingPanel - Composant Speed Dating Heartbeats
 * Événements de speed dating gamifiés
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// 🕐 Composant Compte à Rebours Animé
function CountdownToNextSession({ isDarkMode }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const getNextSaturday20h = () => {
      const now = new Date();
      const nextSaturday = new Date(now);
      // Trouver le prochain samedi
      const daysUntilSaturday = (6 - now.getDay() + 7) % 7 || 7;
      nextSaturday.setDate(now.getDate() + daysUntilSaturday);
      nextSaturday.setHours(20, 0, 0, 0);
      // Si c'est samedi et qu'il est déjà 20h passé, prendre le samedi suivant
      if (nextSaturday <= now) {
        nextSaturday.setDate(nextSaturday.getDate() + 7);
      }
      return nextSaturday;
    };
    
    const target = getNextSaturday20h();
    
    const interval = setInterval(() => {
      const now = new Date();
      const diff = target - now;
      
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="flex justify-center gap-2">
      {[
        { value: timeLeft.days, label: 'Jours' },
        { value: timeLeft.hours, label: 'Heures' },
        { value: timeLeft.minutes, label: 'Min' },
        { value: timeLeft.seconds, label: 'Sec' }
      ].map((item, idx) => (
        <div key={idx} className="flex flex-col items-center">
          <motion.div
            key={`${idx}-${item.value}`}
            initial={{ scale: 1.1, opacity: 0.5 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className={`w-14 h-14 rounded-xl flex items-center justify-center text-xl font-black ${
              isDarkMode 
                ? 'bg-gradient-to-br from-pink-600 to-purple-600 text-white' 
                : 'bg-gradient-to-br from-pink-500 to-orange-500 text-white shadow-lg'
            }`}
          >
            {String(item.value).padStart(2, '0')}
          </motion.div>
          <span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SpeedDatingPanel({ isDarkMode, customerId, profile }) {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadStatus();
    loadConfig();
  }, []);
  
  useEffect(() => {
    if (status?.status === 'active' && sessionId) {
      loadCurrentPartner();
      const interval = setInterval(loadCurrentPartner, 5000);
      return () => clearInterval(interval);
    }
  }, [status, sessionId]);
  
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);
  
  const loadStatus = async () => {
    try {
      const res = await api.getSpeedDatingStatus();
      setStatus(res);
      if (res.sessionId) setSessionId(res.sessionId);
      if (res.countdown) setTimeLeft(Math.floor(res.countdown));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadConfig = async () => {
    try {
      const res = await api.getSpeedDatingConfig();
      setConfig(res);
    } catch (error) {
      console.error(error);
    }
  };
  
  const loadCurrentPartner = async () => {
    if (!sessionId) return;
    try {
      const res = await api.getCurrentSpeedDatingPartner(sessionId, customerId);
      setCurrentPartner(res);
      if (res.roundEndTime) {
        const endTime = new Date(res.roundEndTime);
        const now = new Date();
        setTimeLeft(Math.max(0, Math.floor((endTime - now) / 1000)));
      }
    } catch (error) {
      console.error(error);
    }
  };
  
  const handleJoin = async () => {
    try {
      const res = await api.joinSpeedDating(customerId);
      toast.success(`🎉 ${res.message}`);
      setSessionId(res.sessionId);
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur inscription');
    }
  };
  
  const handleVote = async (vote) => {
    if (!currentPartner?.partner) return;
    try {
      const res = await api.voteSpeedDating(sessionId, customerId, currentPartner.partner.customerId, vote);
      if (res.match) {
        confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 } });
        toast.success(
          res.coupDeFoudre 
            ? `💘 COUP DE FOUDRE avec ${currentPartner.partner.displayName} ! +${res.reward} 👑`
            : `❤️ C'est un MATCH avec ${currentPartner.partner.displayName} ! +${res.reward} 👑`
        );
      } else {
        toast.info(vote ? '❤️ Vote enregistré !' : '❌ Suivant...');
      }
    } catch (error) {
      toast.error('Erreur vote');
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // Vue Speed Dating actif
  if (status?.status === 'active' && currentPartner?.partner) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-pink-500">⚡ SPEED DATING EN COURS</div>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Round {currentPartner.currentRound}/{currentPartner.totalRounds}
          </div>
        </div>
        
        {/* Timer */}
        <div className="flex justify-center">
          <div className={`text-4xl font-mono font-bold px-6 py-3 rounded-xl ${
            timeLeft < 30 ? 'bg-red-500 text-white animate-pulse' : isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-100'
          }`}>
            ⏱️ {formatTime(timeLeft)}
          </div>
        </div>
        
        {/* Partenaire actuel */}
        <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
          {currentPartner.partner.mainPhoto ? (
            <img
              src={currentPartner.partner.mainPhoto}
              alt={currentPartner.partner.displayName}
              className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-pink-500"
            />
          ) : (
            <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center text-6xl">
              {currentPartner.partner.avatar}
            </div>
          )}
          
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentPartner.partner.displayName}
          </h3>
          
          {currentPartner.partner.bio && (
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              "{currentPartner.partner.bio}"
            </p>
          )}
          
          {currentPartner.partner.interests?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {currentPartner.partner.interests.slice(0, 4).map((interest, i) => (
                <span
                  key={i}
                  className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                >
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Boutons de vote */}
        <div className="flex justify-center gap-8">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleVote(false)}
            className="w-20 h-20 rounded-full bg-gray-500 hover:bg-gray-600 text-white text-3xl flex items-center justify-center shadow-lg"
          >
            ❌
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleVote(true)}
            className="w-20 h-20 rounded-full bg-gradient-to-r from-pink-500 to-red-500 text-white text-3xl flex items-center justify-center shadow-lg"
          >
            ❤️
          </motion.button>
        </div>
        
        <p className={`text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Vote en moins de 30s pour un badge "Coup de foudre" 💘
        </p>
      </div>
    );
  }
  
  // Vue en attente / prochaine session
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-5xl mb-3">⚡</div>
        <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Speed Dating LPE
        </h3>
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          3 min pour séduire, 5 rencontres, des matchs instantanés !
        </p>
      </div>
      
      {/* Prochaine session avec COMPTE À REBOURS ANIMÉ */}
      <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-pink-50 to-orange-50'} text-center`}>
        {status?.status === 'waiting' ? (
          <>
            <div className="text-lg font-bold text-green-500 mb-2">🟢 SESSION OUVERTE</div>
            <div className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {status.participants} participants
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              En attente de plus de participants...
            </p>
          </>
        ) : (
          <>
            <div className={`text-xs uppercase tracking-wide mb-2 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
              ⏰ Prochain Speed Dating dans
            </div>
            
            {/* COMPTE À REBOURS ANIMÉ */}
            <CountdownToNextSession isDarkMode={isDarkMode} />
            
            <div className={`text-lg font-bold mt-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Samedi 20h 🍷
            </div>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              📍 À La Petite Épicerie
            </p>
          </>
        )}
      </div>
      
      {/* Règles */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} space-y-3`}>
        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>📋 Comment ça marche ?</h4>
        <div className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <div>⏱️ <strong>3 minutes</strong> par conversation</div>
          <div>👥 <strong>5 personnes</strong> rencontrées</div>
          <div>❤️ Vote ❤️ ou ❌ à la fin de chaque round</div>
          <div>💘 Double ❤️ = <strong>Match instantané !</strong></div>
          <div>👑 <strong>+30 couronnes</strong> par match</div>
          <div>💎 Vote en &lt;30s = Badge <strong>"Coup de foudre"</strong></div>
        </div>
      </div>
      
      {/* Bouton inscription */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleJoin}
        disabled={!profile?.isVisible}
        className={`w-full py-4 rounded-xl font-bold text-lg text-white ${
          profile?.isVisible 
            ? 'bg-gradient-to-r from-pink-500 to-orange-500' 
            : 'bg-gray-400 cursor-not-allowed'
        }`}
      >
        {profile?.isVisible ? '⚡ Rejoindre le Speed Dating' : '👤 Active ton profil d\'abord'}
      </motion.button>
      
      <p className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        100% GRATUIT • +5 👑 juste pour participer
      </p>
    </div>
  );
}

export default SpeedDatingPanel;
